import * as React from "react";
import { Formik, Form, Field } from "formik";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import { IOrderStatus, IPartnerOrder } from "../../../../../interfaces/order";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import moment from "moment";
import { useSelector } from "react-redux";
import { orderRequestModel } from "../../../../../api/apiRequestObjects";
import apis from "../../../../../api/apis";
import { notifyError } from "../../../../../config/toastNotification";
import { APIResponseType } from "../../../../../config/enumApiResponseTypes";
import OrderDetails from "../../../orderDetails/OrderDetails";

// styles
import styles from "./SearchBox.module.scss";

// icons
import SearchIcon from "@material-ui/icons/Search";

interface IProps {
  toggle: () => void;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  posOrderStatus: IOrderStatus[];
  updateOrder: (
    updatedOrderStatus: IOrderStatus,
    selectedOrder: IPartnerOrder
  ) => void;
  handleBillPrinting: (
    partnerOrderId: number | undefined,
    orderReference: string | undefined
  ) => void;
}

const SearchBox: React.FC<IProps> = ({
  toggle,
  searchValue,
  setSearchValue,
  posOrderStatus,
  updateOrder,
  handleBillPrinting,
}) => {
  const initialValues = { searchValue };
  const [searchedOrderList, setSearchedOrderList] = React.useState<
    IPartnerOrder[]
  >([]);
  const [selectedSearchedOrder, setSelectedSearchedOrder] =
    React.useState<IPartnerOrder | null>(null);
  const [msgForUser, setMsgForUser] = React.useState<string>("");

  //get redux state
  const { user } = useSelector((state: any) => state.authentication);

  const onSubmit = (values: { searchValue: string }) => {
    setSearchValue(values.searchValue);
    toggle();
  };

  //handle search when user typing
  const handleOnChangeInputField = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;

    setSelectedSearchedOrder(null);

    if (value.length > 1) {
      setMsgForUser("Searching...");
      try {
        orderRequestModel.UID.UserKey = user.UserKey;
        orderRequestModel.UID.LocationKey = user.LocationKey;
        orderRequestModel.UID.CompanyKey = user.CompanyKey;
        orderRequestModel.UID.EnvironmentName = user.EnvironmentName;

        orderRequestModel.OrderReference = value;

        const response = await apis.Orders.searchByOrderReference(
          orderRequestModel
        );

        if (response.ResponseType === APIResponseType.Success) {
          setMsgForUser("");
          setSearchedOrderList(response.ResponseData.SearchResultedOrderList);
        } else if (response.ResponseType === APIResponseType.NotAvailable) {
          setMsgForUser("No Result Found !");
        } else if (response.ResponseType === APIResponseType.Error) {
          setMsgForUser("Internal Server Error Detected !!!");
        }
      } catch (err) {
        setMsgForUser("");
        notifyError();
      }
    } else {
      setMsgForUser("Please enter 2 or more characters to begin the search");
    }
  };

  // handle when user click searched order
  const handleSelectedOrder = (searchedOrder: IPartnerOrder) => {
    setMsgForUser("");

    let searchedOrderObject = searchedOrder;

    setSearchedOrderList([]);

    setSelectedSearchedOrder(searchedOrderObject);
  };

  return (
    <div className={styles.search_wrapper}>
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        <Form autoComplete="off">
          <input
            name="searchValue"
            type="text"
            placeholder="Search By Order Id"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleOnChangeInputField(e)
            }
          />
          <button type="submit" disabled>
            <SearchIcon />
          </button>
        </Form>
      </Formik>
      {msgForUser === "" &&
        searchedOrderList &&
        searchedOrderList.length > 0 &&
        searchedOrderList.map((searchedOrder) => (
          <List sx={{ width: "100%", bgcolor: "background.paper" }}>
            <ListItem key={searchedOrder.OrderId}>
              <ListItemButton
                onClick={() => handleSelectedOrder(searchedOrder)}
              >
                <ListItemAvatar>
                  <Avatar
                    alt={searchedOrder.OrderDelivery.TypeOfOrder.OrderTypeCode}
                    src={
                      process.env.PUBLIC_URL +
                      `/PartnerLogos/${searchedOrder.OrderDelivery.TypeOfOrder.OrderTypeCode}.svg`
                    }
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={`Order ID: ${searchedOrder.OrderReference}`}
                  secondary={
                    <React.Fragment>
                      <Typography
                        sx={{ display: "inline" }}
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        {`QTY: ${searchedOrder.Quantity.toFixed(
                          3
                        )}   AMT: ${searchedOrder.Amount.toFixed(2)} `}
                        {"\n"}
                      </Typography>
                      <Typography
                        sx={{ display: "block" }}
                        variant="caption"
                        gutterBottom
                        color="text.secondary"
                      >
                        {moment(searchedOrder.OrderDate).format(
                          "DD-MM-YYYY, h:mm:ss a"
                        )}
                      </Typography>
                    </React.Fragment>
                  }
                />
              </ListItemButton>
            </ListItem>
          </List>
        ))}
      {msgForUser === "" &&
        selectedSearchedOrder &&
        selectedSearchedOrder !== null && (
          <OrderDetails
            selectedItem={selectedSearchedOrder}
            updateOrder={updateOrder}
            posOrderStatus={posOrderStatus}
            handleBillPrinting={handleBillPrinting}
          />
        )}
      {msgForUser !== "" && (
        <div className={styles.search_info}>{msgForUser}</div>
      )}
    </div>
  );
};

export default SearchBox;
